import React, { forwardRef, useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { isEqual } from 'lodash';

import {
	TextField,
	TextFieldProps,
	Checkbox,
	Typography,
	MenuItem,
	Theme,
	Chip,
	Box,
	IconButton,
	Popover,
	InputAdornment,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Autocomplete, {
	AutocompleteRenderInputParams,
	AutocompleteRenderGroupParams,
	AutocompleteCloseReason,
	AutocompleteChangeReason,
	AutocompleteChangeDetails,
	AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';
import {
	RiCheckboxBlankLine,
	RiCheckboxLine,
	RiCloseLine,
	RiArrowDownSFill,
	RiSearchLine,
} from 'react-icons/ri';

import { ProductApi } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import * as StockSelectors from 'selectors/StockSelectors';
import * as LanguageSelectors from 'selectors/LanguageSelectors';

import {
	Option,
	getOptionsForActiveCategories,
	getOptionsForProducts,
	getOptionsForCategory,
	filterOptions,
	getProductsFromOptions,
	getOptionLabel,
	getOptionCategory,
} from '../utils/optionUtils';

const icon = <RiCheckboxBlankLine size={20} />;
const checkedIcon = <RiCheckboxLine size={20} />;

interface Props {
	value?: ProductApi[] | undefined;
	onChange?: (value: ProductApi[]) => void;
	TextFieldProps?: TextFieldProps;
	isLiftTicketsFiltered?: boolean;
}

const ProductsWithCategorySelect = forwardRef((props: Props, ref) => {
	const { value = [], onChange, TextFieldProps, isLiftTicketsFiltered } = props;
	const { t, getTranslation } = useTranslation();

	const allActiveLocationProducts = useSelector(StockSelectors.activeLocationProducts);
	const liftTicketFilteredProducts = allActiveLocationProducts.filter(
		(product) => product.external !== 'LIFT_TICKET',
	);
	const activeLocationProducts = !isLiftTicketsFiltered
		? allActiveLocationProducts
		: liftTicketFilteredProducts;

	const categories = useSelector(StockSelectors.categoriesData);
	const lang = useSelector(LanguageSelectors.language);
	const options = useMemo(
		() => getOptionsForActiveCategories(categories, activeLocationProducts, lang),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[categories, lang],
	);

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [open, setOpen] = useState(false);
	const [valueOptions, setValueOptions] = useState<Option[]>(getOptionsForProducts(value, options));
	const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
	const [searchInput, setSearchInput] = useState<string>('');

	useEffect(() => {
		setValueOptions(getOptionsForProducts(value, options));
	}, [value, options]);

	const classes = useStyles({ width: anchorEl?.clientWidth });
	const isAllOptionsSelected = options.length > 0 && valueOptions.length === options.length;
	const isAllFilteredOptionsSelected =
		filteredOptions.length > 0 &&
		filteredOptions.every((option) => valueOptions.find((o) => o.id === option.id));
	const isAllSelected = searchInput ? isAllFilteredOptionsSelected : isAllOptionsSelected;

	const handleClose = (_event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
		if (reason === 'toggleInput') {
			return;
		}
		const updatedValue = getProductsFromOptions(activeLocationProducts, valueOptions);
		if (!isEqual(updatedValue, value)) {
			onChange?.(updatedValue);
		}
	};

	const handleChange = (
		_event: React.ChangeEvent<{}>,
		_selectedOptions: Option[],
		reason: AutocompleteChangeReason,
		details?: AutocompleteChangeDetails<Option>,
	) => {
		if (reason === 'selectOption' && details) {
			const addedOptions = options.filter((option) => option.id === details.option.id);
			setValueOptions([...valueOptions, ...addedOptions]);
		}
		if (reason === 'removeOption' && details) {
			const updatedValueOptions = valueOptions.filter((option) => option.id !== details.option.id);
			setValueOptions(updatedValueOptions);
		}
	};

	const handleInputChange = (
		_event: React.ChangeEvent<{}>,
		inputValue: string,
		reason: AutocompleteInputChangeReason,
	) => {
		if (reason === 'reset') {
			return;
		}
		setSearchInput(inputValue);
		const newFilteredOptions = filterOptions(options, { inputValue, getOptionLabel });
		if (!isEqual(filteredOptions, newFilteredOptions)) {
			setFilteredOptions(newFilteredOptions);
		}
	};

	const handleOpenPopup = () => {
		setSearchInput('');
		setFilteredOptions(options);
		setOpen(true);
	};

	const handleClosePopup = () => setOpen(false);

	const handleClearAll = () => {
		setValueOptions([]);
		onChange?.([]);
	};

	const handleDeleteTag = (productId?: string) => {
		if (!productId) {
			setValueOptions([]);
			onChange?.([]);
			return;
		}
		const updatedValueOptions = valueOptions.filter((option) => option.id !== productId);
		const updatedValue = value.filter((product) => product.id !== productId);
		setValueOptions(updatedValueOptions);
		onChange?.(updatedValue);
	};

	const isAllCategoryOptionsSelected = (categoryId: string) => {
		const categoryOptions = getOptionsForCategory(filteredOptions, categoryId);
		return categoryOptions.every((option) => valueOptions.find((o) => o.id === option.id));
	};

	const handleSelectAllCategoryOptions = (categoryId: string) => {
		const categoryOptions = getOptionsForCategory(filteredOptions, categoryId);
		const unselectedCategoryOptions = filteredOptions.filter(
			(option) =>
				categoryOptions.find((o) => o.id === option.id) &&
				!valueOptions.find((o) => o.id === option.id),
		);
		const updatedValueOptions = [...valueOptions, ...unselectedCategoryOptions];
		setValueOptions(updatedValueOptions);
	};

	const handleUnselectAllCategoryOptions = (categoryId: string) => {
		const categoryOptions = getOptionsForCategory(filteredOptions, categoryId);
		const updatedValueOptions = valueOptions.filter(
			(option) => !categoryOptions.find((o) => o.id === option.id),
		);
		setValueOptions(updatedValueOptions);
	};

	const handleCategoryClick = (categoryId: string) =>
		isAllCategoryOptionsSelected(categoryId)
			? handleUnselectAllCategoryOptions(categoryId)
			: handleSelectAllCategoryOptions(categoryId);

	const handleSelectAll = () => {
		if (!searchInput) {
			setValueOptions(options);
			return;
		}
		const unselectedCategoryOptions = filteredOptions.filter(
			(option) => !valueOptions.find((o) => o.id === option.id),
		);
		const updatedValueOptions = [...valueOptions, ...unselectedCategoryOptions];
		setValueOptions(updatedValueOptions);
	};

	const handleUnselectAll = () => {
		if (!searchInput) {
			setValueOptions([]);
			return;
		}
		const updatedValueOptions = valueOptions.filter(
			(option) => !filteredOptions.find((o) => o.id === option.id),
		);
		setValueOptions(updatedValueOptions);
	};

	const handleSelectAllClick = isAllSelected ? handleUnselectAll : handleSelectAll;

	const renderTags = () => {
		const products = !open ? value : getProductsFromOptions(activeLocationProducts, valueOptions);

		return (
			<>
				{isAllOptionsSelected ? (
					<Chip
						label={t('common:products.allProducts')}
						onDelete={() => handleDeleteTag()}
						className={classes.chip}
					/>
				) : (
					products.map((product) => (
						<Chip
							key={product.id}
							label={getTranslation(product.name)}
							onDelete={() => handleDeleteTag(product.id)}
							className={classes.chip}
						/>
					))
				)}
			</>
		);
	};

	const renderEndAdornment = () => (
		<Box className={classes.endAdornment}>
			{valueOptions.length ? (
				<IconButton className={classes.closeIcon} onClick={handleClearAll} size="large">
					<RiCloseLine size={22} />
				</IconButton>
			) : null}

			<IconButton
				onClick={handleOpenPopup}
				className={classNames(classes.popupIndicator, {
					[classes.popupIndicatorOpen]: open,
				})}
				size="large"
			>
				<RiArrowDownSFill size={20} />
			</IconButton>
		</Box>
	);

	const renderOption = (
		props: React.HTMLAttributes<HTMLLIElement>,
		option: Option,
		{ selected }: { selected: boolean },
	) => (
		<MenuItem key={option.id} {...props}>
			<Checkbox
				icon={icon}
				color="primary"
				checkedIcon={checkedIcon}
				className={classes.productCheckbox}
				checked={selected}
			/>
			<Typography variant="body1">{option.label}</Typography>
		</MenuItem>
	);

	const renderGroup = (params: AutocompleteRenderGroupParams) => {
		const categoryLabel =
			options.find((option) => option.categoryId === params.group)?.categoryLabel ?? '';
		const isCategorySelected = isAllCategoryOptionsSelected(params.group);

		return (
			<React.Fragment key={params.key}>
				<MenuItem
					onClick={() => handleCategoryClick(params.group)}
					className={classNames(classes.category, isCategorySelected && classes.selectedCategory)}
				>
					<Checkbox
						icon={icon}
						color="primary"
						checkedIcon={checkedIcon}
						className={classes.categoryCheckbox}
						checked={isCategorySelected}
					/>
					<Typography variant="body1">{categoryLabel}</Typography>
				</MenuItem>
				{params.children}
			</React.Fragment>
		);
	};

	const renderInput = (params: AutocompleteRenderInputParams) => (
		<>
			<TextField
				ref={params.InputProps.ref}
				inputProps={params.inputProps}
				placeholder={t('rentals.search.byProductName')}
				fullWidth
				autoFocus
				margin="normal"
				className={classes.inputSearchRoot}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<RiSearchLine size={20} />
						</InputAdornment>
					),
				}}
				onKeyDown={(e) => e.stopPropagation()}
			/>
			{options.length > 1 && !searchInput && (
				<MenuItem
					onClick={handleSelectAllClick}
					className={classNames(classes.category, isAllSelected && classes.selectedCategory)}
				>
					<Checkbox
						icon={icon}
						color="primary"
						checkedIcon={checkedIcon}
						className={classes.categoryCheckbox}
						checked={isAllSelected}
					/>
					<Typography variant="body1">{t('common:actions.selectAll')}</Typography>
				</MenuItem>
			)}
		</>
	);

	return (
		<>
			<TextField
				variant="standard"
				label={t('product.product', 'Product')}
				placeholder={!valueOptions.length ? t('product.selectProduct', 'Select product') : ''}
				fullWidth
				ref={setAnchorEl}
				value=""
				InputProps={{
					startAdornment: renderTags(),
					endAdornment: renderEndAdornment(),
					className: classes.inputRoot,
				}}
				inputProps={{
					className: classes.input,
					onClick: handleOpenPopup,
				}}
				{...TextFieldProps}
			/>
			<Popover
				open={open}
				onClose={handleClosePopup}
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				classes={{
					paper: classes.popoverPaper,
				}}
			>
				<Autocomplete
					open={open}
					autoComplete
					autoHighlight
					disablePortal
					disableCloseOnSelect
					multiple
					id="products-with-category-select"
					options={options}
					value={valueOptions}
					inputValue={searchInput}
					getOptionLabel={getOptionLabel}
					groupBy={getOptionCategory}
					filterOptions={filterOptions}
					onClose={handleClose}
					onChange={handleChange}
					onInputChange={handleInputChange}
					renderGroup={renderGroup}
					renderOption={renderOption}
					renderInput={renderInput}
					classes={{
						paper: classes.autocompletePaper,
						popperDisablePortal: classes.popperDisablePortal,
						listbox: classes.listbox,
						option: classes.option,
					}}
				/>
			</Popover>
		</>
	);
});

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		productCheckbox: {
			marginLeft: 38,
			marginRight: theme.spacing(1),
		},
		categoryCheckbox: {
			marginRight: theme.spacing(1),
		},
		chip: {
			margin: theme.spacing(1, 0.25),
		},
		endAdornment: {
			top: '50%',
			transform: 'translate(0, -50%)',
			right: 0,
			position: 'absolute',
		},
		closeIcon: {
			marginRight: theme.spacing(-0.25),
			padding: theme.spacing(0.5),
			color: theme.palette.action.active,
		},
		popupIndicator: {
			padding: theme.spacing(0.25),
			marginRight: theme.spacing(-0.25),
			marginLeft: theme.spacing(0.5),
			color: theme.palette.action.active,
		},
		popupIndicatorOpen: {
			transform: 'rotate(180deg)',
		},
		popoverPaper: {
			width: ({ width }: { width?: number }) => width ?? '100%',
			marginTop: theme.spacing(1),
			overflow: 'visible',
		},
		autocompletePaper: {
			boxShadow: 'none',
			margin: 0,
			width: '100%',
		},
		popperDisablePortal: {
			position: 'relative !important' as 'relative', //mui4->mui5 migration caused layout issues for custom rendering
			width: '100%',
			transform: 'translate(0,0) !important', //mui4->mui5 migration caused layout issues for custom rendering
		},
		inputSearchRoot: {
			padding: theme.spacing(0, 3),
			'& input': {
				backgroundColor: theme.palette.background.paper,
				padding: theme.spacing(1),
			},
		},
		inputRoot: {
			paddingRight: 60,
			flexWrap: 'wrap',
			position: 'relative',
		},
		input: {
			width: 0,
			minWidth: 30,
			flexGrow: 1,
		},
		//TODO: Check how these work in practice
		option: {
			'&[data-focus="true"]': {
				backgroundColor: 'transparent',
			},
			[`&[aria-selected="true"], &[aria-selected="true"]&[data-focus="true"]`]: {
				backgroundColor: theme.palette.action.focus,
			},
			[`&:hover, &[aria-selected="true"]&:hover`]: {
				backgroundColor: theme.palette.action.hover,
			},
		},
		category: {
			backgroundColor: theme.palette.background.secondary,
			'&:hover': {
				backgroundColor: theme.palette.action.hover,
			},
		},
		selectedCategory: {
			backgroundColor: theme.palette.action.selected,
		},
		listbox: {
			maxHeight: '35vh',
			paddingTop: 0,
		},
	}),
);

export default ProductsWithCategorySelect;
